import {
  AloftArea,
  AloftPrice,
  Improvements,
  ORDER_FEATURES,
  OrderFeature,
  PROPERTY_GRID_SUBJECT_PROPERTY,
  getAbbreviatedCarportGarage,
  isDefined,
  orUndefined,
} from '@aloft/toolkit-shared';

import { cloneDeep } from 'lodash';

import { PropertyGridSection } from '../../../components/PropertyGrid/columnSections/PropertyGridSection.tsx';
import { PropertyGridSectionProps } from '../../../components/PropertyGrid/types.ts';
import {
  selectIncludeCostApproach,
  selectOrderFeatures,
  selectSalesComparisonApproach,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { selectImprovementsEntities } from '../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { selectSelectedSourcesEntities } from '../../../redux/features/selectors/propertyDataSelectors/selectedSourcesSelectors.ts';
import { selectReconciliation } from '../../../redux/features/selectors/selectReconciliation.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { ADJUSTMENT_LABELS } from '../../adjustments/internal/PairedCostSettings/internal/consts.ts';
import { ImprovementField, improvementsFieldName } from './consts.ts';
import {
  AdjustmentsFeatureDisplayContext,
  garageCarportFeatureDefs,
  isBasementTreatAsNone,
  poolSpaFeatureDefs,
  porchPatioDeckFeatureDefs,
  resolveAdjustmentsFeatureDisplay,
  shouldDisplayAdjustmentsFeature,
} from './resolveAdjustmentsFeatureDisplay.ts';

export const AdjustmentsGridAdditionalFeaturesSection = (props: PropertyGridSectionProps) => {
  const improvementEntities = useAppSelector(selectImprovementsEntities);
  const selectedSourcesEntities = useAppSelector(selectSelectedSourcesEntities);
  const { includeTotalCostToCure, totalCostToCure } = useAppSelector(selectReconciliation);
  const isUnitRate = useAppSelector(selectSalesComparisonApproach) === 'unit_rate';
  const includeCostApproach = useAppSelector(selectIncludeCostApproach);
  const orderFeatures = useAppSelector(selectOrderFeatures);

  const resolveFeature = (
    improvements: Improvements | undefined,
    propertyId: string,
    isArv: boolean | undefined,
    params: Omit<
      Parameters<typeof resolveAdjustmentsFeatureDisplay>[0],
      'improvements' | 'orderFeatures' | 'selectedSources' | 'isArv'
    >,
  ) =>
    resolveAdjustmentsFeatureDisplay({
      improvements,
      orderFeatures,
      selectedSources: selectedSourcesEntities[propertyId],
      isArv,
      ...params,
    });

  const singleFeatureField = (
    feature: OrderFeature,
    label: string,
    manualAdjustmentKey: ImprovementField['manualAdjustmentKey'],
    unknownPathSuffixes: readonly string[],
    extras?: Partial<ImprovementField>,
  ): ImprovementField => ({
    fieldName: improvementsFieldName,
    label,
    manualAdjustmentKey,
    ...extras,
    toDisplayValue: (improvements, propertyId, { isArv }: AdjustmentsFeatureDisplayContext = {}) =>
      resolveFeature(improvements, propertyId, isArv, {
        orderKeys: [feature],
        featureDefs: [ORDER_FEATURES[feature]],
        unknownPathSuffixes,
        format: () => orUndefined(ORDER_FEATURES[feature].displayValue)(improvements!) ?? '',
      }),
  });

  const featureFields: ImprovementField[] = [];

  const belowGradeHalfBathField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.basementHalfBaths,
    manualAdjustmentKey: 'basementHalfBaths',
    unitRateKey: 'basementHalfBaths',
    unit: 'ba',
    toDisplayValue: (
      improvements,
      propertyId,
      { isArv }: AdjustmentsFeatureDisplayContext = {},
    ) => {
      if (isBasementTreatAsNone(improvements, orderFeatures)) return 'None';
      return resolveFeature(improvements, propertyId, isArv, {
        orderKeys: ['basement'],
        featureDefs: [ORDER_FEATURES.basement],
        unknownPathSuffixes: ['basement.halfBaths'],
        format: () =>
          improvements?.basement?.hasFeature && isDefined(improvements.basement.halfBaths)
            ? `${improvements.basement.halfBaths}`
            : '',
      });
    },
  };

  const belowGradeFullBathField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.basementFullBaths,
    manualAdjustmentKey: 'basementFullBaths',
    unitRateKey: 'basementFullBaths',
    unit: 'ba',
    toDisplayValue: (
      improvements,
      propertyId,
      { isArv }: AdjustmentsFeatureDisplayContext = {},
    ) => {
      if (isBasementTreatAsNone(improvements, orderFeatures)) return 'None';
      return resolveFeature(improvements, propertyId, isArv, {
        orderKeys: ['basement'],
        featureDefs: [ORDER_FEATURES.basement],
        unknownPathSuffixes: ['basement.fullBaths'],
        format: () =>
          improvements?.basement?.hasFeature && isDefined(improvements.basement.fullBaths)
            ? `${improvements.basement.fullBaths}`
            : '',
      });
    },
  };

  const bathBelowGradeField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.bathBelowGrade,
    manualAdjustmentKey: 'bathBelowGrade',
    toDisplayValue: (improvements) => {
      if (isBasementTreatAsNone(improvements, orderFeatures)) return 'None';
      if (
        !isDefined(improvements?.basement?.fullBaths) &&
        !isDefined(improvements?.basement?.halfBaths)
      ) {
        return undefined;
      }

      return `${improvements?.basement?.fullBaths ?? 0}.${improvements?.basement?.halfBaths ?? 0}`;
    },
  };

  const garageCarportField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.garageCarport,
    manualAdjustmentKey: 'garageCarport',
    unitRateKey: 'garageCarport',
    unit: 'spc',
    toDisplayValue: (improvements, propertyId, { isArv }: AdjustmentsFeatureDisplayContext = {}) =>
      resolveFeature(improvements, propertyId, isArv, {
        orderKeys: ['carport', 'detachedGarage', 'garage'],
        featureDefs: garageCarportFeatureDefs,
        unknownPathSuffixes: ['carport.quality', 'detachedGarage.quality', 'garage.quality'],
        format: () => getAbbreviatedCarportGarage(improvements) ?? '',
      }),
  };

  const porchPatioDeckField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.porchPatioDeck,
    manualAdjustmentKey: 'porchPatioDeck',
    toDisplayValue: (improvements, propertyId, { isArv }: AdjustmentsFeatureDisplayContext = {}) =>
      resolveFeature(improvements, propertyId, isArv, {
        orderKeys: ['porch', 'patio', 'deck'],
        featureDefs: porchPatioDeckFeatureDefs,
        unknownPathSuffixes: ['porch.area', 'patio.area', 'deck.area'],
        format: () =>
          [ORDER_FEATURES.porch, ORDER_FEATURES.patio, ORDER_FEATURES.deck]
            .filter((feature) => feature.hasFeature(improvements!))
            .map((feature) => feature.label)
            .join(';'),
      }),
  };

  const atticField = singleFeatureField('attic', ADJUSTMENT_LABELS.attic, 'attic', ['attic.area']);
  const fireplaceField = singleFeatureField('fireplace', ADJUSTMENT_LABELS.fireplace, 'fireplace', [
    'fireplace.type',
    'fireplace.count',
  ]);
  const barnField = singleFeatureField('barn', ADJUSTMENT_LABELS.barn, 'barn', ['barn.area']);
  const aduField = singleFeatureField('adu', ADJUSTMENT_LABELS.adu, 'adu', ['adu.area'], {
    unitRateKey: 'adu',
    unit: 'sf',
  });
  const shedField = singleFeatureField('shed', ADJUSTMENT_LABELS.shed, 'shed', ['shed.area']);
  const workshopField = singleFeatureField('workshop', ADJUSTMENT_LABELS.workshop, 'workshop', [
    'workshop.area',
  ]);

  const basementTotalField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.basementTotal,
    manualAdjustmentKey: 'basementTotal',
    unitRateKey: 'basementTotal',
    unit: 'sf',
    toDisplayValue: (
      improvements,
      propertyId,
      { isArv }: AdjustmentsFeatureDisplayContext = {},
    ) => {
      if (isBasementTreatAsNone(improvements, orderFeatures)) return 'None';
      return resolveFeature(improvements, propertyId, isArv, {
        orderKeys: ['basement'],
        featureDefs: [ORDER_FEATURES.basement],
        unknownPathSuffixes: ['basement.totalArea'],
        format: () => {
          const { totalArea } = improvements?.basement ?? {};
          if (!totalArea || !ORDER_FEATURES.basement.hasFeature(improvements!)) return '';
          return AloftArea.schema.parse(totalArea).getSiteAreaString('');
        },
      });
    },
  };

  const basementFinishedField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.basementFinished,
    manualAdjustmentKey: 'basementFinished',
    unitRateKey: 'basementFinished',
    unit: 'sf',
    toDisplayValue: (
      improvements,
      propertyId,
      { isArv }: AdjustmentsFeatureDisplayContext = {},
    ) => {
      if (isBasementTreatAsNone(improvements, orderFeatures)) return 'None';
      return resolveFeature(improvements, propertyId, isArv, {
        orderKeys: ['basement'],
        featureDefs: [ORDER_FEATURES.basement],
        unknownPathSuffixes: ['basement.finishedArea'],
        format: () => {
          const { finishedArea } = improvements?.basement ?? {};
          if (!finishedArea || !ORDER_FEATURES.basement.hasFeature(improvements!)) return '';
          return AloftArea.schema.parse(finishedArea).getSiteAreaString('');
        },
      });
    },
  };

  const basementField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.basement,
    manualAdjustmentKey: 'basement',
    toDisplayValue: (
      improvements,
      propertyId,
      { isArv }: AdjustmentsFeatureDisplayContext = {},
    ) => {
      if (isBasementTreatAsNone(improvements, orderFeatures)) return 'None';
      return resolveFeature(improvements, propertyId, isArv, {
        orderKeys: ['basement'],
        featureDefs: [ORDER_FEATURES.basement],
        unknownPathSuffixes: ['basement.totalArea', 'basement.finishedArea'],
        format: () => orUndefined(ORDER_FEATURES.basement.displayValue)(improvements!) ?? '',
      });
    },
  };

  const poolSpaField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.poolSpa,
    manualAdjustmentKey: 'poolSpa',
    toDisplayValue: (improvements, propertyId, { isArv }: AdjustmentsFeatureDisplayContext = {}) =>
      resolveFeature(improvements, propertyId, isArv, {
        orderKeys: ['pool', 'spa'],
        featureDefs: poolSpaFeatureDefs,
        unknownPathSuffixes: ['pool.area', 'spa.area'],
        format: () => {
          const poolDisplay = orUndefined(ORDER_FEATURES.pool.displayValue)(improvements!);
          const spaDisplay = orUndefined(ORDER_FEATURES.spa.displayValue)(improvements!);
          return [
            poolDisplay ? `${poolDisplay} Pool` : undefined,
            spaDisplay ? `${spaDisplay} Spa` : undefined,
          ]
            .filter(isDefined)
            .join('; ');
        },
      }),
  };

  const solarPanelField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.solarPanel,
    manualAdjustmentKey: 'solarPanel',
    toDisplayValue: (improvements, propertyId, { isArv }: AdjustmentsFeatureDisplayContext = {}) =>
      resolveFeature(improvements, propertyId, isArv, {
        orderKeys: ['solarPanel'],
        featureDefs: [ORDER_FEATURES.solarPanel],
        unknownPathSuffixes: ['solarPanel.ownership'],
        format: () => orUndefined(ORDER_FEATURES.solarPanel.displayValue)(improvements!) ?? '',
      }),
  };

  const manualCostField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.manual,
    manualAdjustmentKey: 'manual',
    toDisplayValue: (improvements) =>
      orUndefined(ORDER_FEATURES.manualCost.displayValue)(improvements),
  };

  const shouldDisplayFeatureField = (featureDependencies: OrderFeature[]) =>
    shouldDisplayAdjustmentsFeature({
      featureDependencies,
      orderFeatures,
      improvementEntities,
    });

  const shouldDisplayManualCostField =
    includeCostApproach &&
    Object.values(improvementEntities).some(
      (improvements) =>
        ORDER_FEATURES.manualCost.hasFeature(improvements) ||
        (improvements?.hasManualCost ?? false) ||
        Boolean(improvements?.manualCostDisplayName?.trim()),
    );

  if (shouldDisplayFeatureField(['carport', 'detachedGarage', 'garage'])) {
    featureFields.push(garageCarportField);
  }
  if (shouldDisplayFeatureField(['porch', 'patio', 'deck'])) {
    featureFields.push(porchPatioDeckField);
  }
  if (shouldDisplayFeatureField(['attic'])) featureFields.push(atticField);
  if (shouldDisplayFeatureField(['fireplace'])) featureFields.push(fireplaceField);
  if (shouldDisplayFeatureField(['barn'])) featureFields.push(barnField);
  if (shouldDisplayFeatureField(['adu'])) featureFields.push(aduField);
  if (orderFeatures.includes('basement')) {
    if (isUnitRate) {
      featureFields.push(
        basementTotalField,
        basementFinishedField,
        belowGradeFullBathField,
        belowGradeHalfBathField,
      );
    } else {
      featureFields.push(basementField, bathBelowGradeField);
    }
  }
  if (shouldDisplayFeatureField(['pool', 'spa'])) featureFields.push(poolSpaField);
  if (shouldDisplayFeatureField(['shed'])) featureFields.push(shedField);
  if (shouldDisplayFeatureField(['workshop'])) featureFields.push(workshopField);
  if (shouldDisplayFeatureField(['solarPanel'])) featureFields.push(solarPanelField);
  if (shouldDisplayManualCostField) featureFields.push(manualCostField);

  const totalCostToCureField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.totalCostToCure,
    manualAdjustmentKey: 'totalCostToCure',
    tooltipContent:
      'Major items only (i.e. soundness, security, livability). No cosmetic or minor deferred maintenance.',
    toDisplayValue: (improvements, propertyId) => {
      if (propertyId !== PROPERTY_GRID_SUBJECT_PROPERTY) {
        return improvements?.totalCostToCureDisplayName;
      }

      return orUndefined(AloftPrice.schema.parse)(totalCostToCure)?.toString(true);
    },
    toEditValue: (improvements, propertyId) => {
      if (propertyId !== PROPERTY_GRID_SUBJECT_PROPERTY) {
        return improvements?.totalCostToCureDisplayName;
      }

      return orUndefined(AloftPrice.schema.parse)(totalCostToCure)?.toDollarLike().toString();
    },
    toValue: (value: string | undefined, propertyId: string) => {
      const improvements = cloneDeep(improvementEntities[propertyId]);
      if (!improvements) return undefined;

      if (propertyId !== PROPERTY_GRID_SUBJECT_PROPERTY) {
        improvements.totalCostToCureDisplayName = value;
      }

      return improvements;
    },
  };

  const otherField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.other,
    manualAdjustmentKey: 'other',
    toDisplayValue: (improvements) => improvements?.otherAdjustmentsDisplayName,
    toEditValue: (improvements) => improvements?.otherAdjustmentsDisplayName,
    toValue: (value: string | undefined, propertyId: string) => {
      const improvements = cloneDeep(improvementEntities[propertyId]);
      if (!improvements) return undefined;

      improvements.otherAdjustmentsDisplayName = value;
      return improvements;
    },
  };

  return (
    <PropertyGridSection
      {...props}
      title="Additional Features"
      hasSeparateArvValue
      fields={[
        ...featureFields,
        ...(includeTotalCostToCure ? [totalCostToCureField] : []),
        otherField,
      ]}
    />
  );
};
